var _AnalyticsProxy;
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { startTransition } from 'react';
import pixall from './pixall';
import googleAnalytics from './googleAnalytics';
import isSSR from '../utils/isSSR';
import clientConfig from '@kbbsdk/config/clientConfig';
import isValidPostMessageOrigin from '../utils/isValidPostMessageOrigin';
import { getAnalyticsMessage } from './utils/googleAnalytics/getAnalyticsMessage';
import StateManager from '../common/managers/StateManager';
import getOptionalValue from '../utils/getOptionalValue';
import { isFlipperOn } from '../utils/flipperExtensions';
import getFlippersFromManager from '../utils/getFlippersFromManager';
import { getPixallScripts } from './react/usePixallScripts';
import { getGtmScripts } from '../ads/useGtmScripts';
export default class AnalyticsProxy extends StateManager {
  constructor({
    instanceId = AnalyticsProxy.InstanceId,
    pathToData = '__ANALYTICS_PROXY_CONFIG__'
  } = {}) {
    super({
      instanceId,
      pathToData
    });
    _defineProperty(this, "initialized", false);
    _defineProperty(this, "isFirstLoad", false);
    _defineProperty(this, "currentWindow", null);
    _defineProperty(this, "lastTimestamp", null);
    _defineProperty(this, "currentSection", null);
    _defineProperty(this, "targets", {});
    _defineProperty(this, "queue", []);
    _defineProperty(this, "getSerializableData", () => {
      return {
        lastTimestamp: this.lastTimestamp
      };
    });
    _defineProperty(this, "getSerializedScript", () => {
      return Object.keys(this.currentWindow).map(entry => `
      window.${entry} = window.${entry} || ${JSON.stringify(this.currentWindow[entry])};
    `).join('\n');
    });
    _defineProperty(this, "getHeadScriptContent", () => ``);
    _defineProperty(this, "getHeadScript", () => ``);
    _defineProperty(this, "onDOMPageReady", () => {
      if (window.getDataLayer && window.getDataLayer()) {
        this.onPageReady(window.getDataLayer());
      }
    });
    _defineProperty(this, "onMessage", event => {
      if (!isValidPostMessageOrigin(event.origin)) {
        return;
      }
      const analyticsEvent = getAnalyticsMessage(event);
      if (analyticsEvent) {
        this.push(() => analyticsEvent);
      }
    });
    _defineProperty(this, "getRequiredScripts", () => {
      const enabledFlippers = getFlippersFromManager(this);
      return [...getPixallScripts({
        enabledFlippers
      }), ...getGtmScripts({
        enabledFlippers,
        query: this.context?.query,
        cookies: this.context?.cookies
      })];
    });
    this.initialized = !isSSR() && window.getDataLayer && window.getDataLayer() && window.getDataLayer().isReady;
    this.isFirstLoad = isSSR() || !getOptionalValue(window, this.pathToData);
    this.currentWindow = !isSSR() ? window : {};
    if (clientConfig.enableInlinePixall) {
      this.targets.pixall = pixall;
    }
    if (clientConfig.enableGoogleAnalytics) {
      this.targets.googleAnalytics = googleAnalytics;
    }
    if (!isSSR()) {
      Object.assign(this, getOptionalValue(window, this.pathToData) || {});
    }
    Object.keys(this.targets).forEach(targetName => {
      const target = this.targets[targetName];
      if (target && target.initialize) {
        target.initialize(this.currentWindow);
      }
    });
    if (!isSSR()) {
      this.onDOMPageReady();
      document.body.addEventListener('DataLayerReady', this.onDOMPageReady);
      window.addEventListener('message', this.onMessage);
      document.body.addEventListener('ForcePageView', this.onDOMPageReady);
    }
  }
  getScriptTag() {
    if (isSSR()) {
      return `
        <script>
          // Analytics Queues
          window.${this.pathToData} = ${JSON.stringify(this.getSerializableData())};
          ${this.getSerializedScript()}
        </script>
      `;
    }
  }
  onPageReady(dataLayer) {
    if (dataLayer.isReady && this.lastTimestamp !== dataLayer.timestamp) {
      const {
        isFirstLoad
      } = this;
      const pixallPageView = {
        pixall: {
          isInitialLoad: isFirstLoad
        }
      };
      this.push(() => ({
        type: "pageView",
        ...(!!dataLayer?.excludePixallPageCall ? {} : pixallPageView),
        googleAnalytics: {}
      }));
    }
    this.evaluateQueue(dataLayer);
    this.lastTimestamp = dataLayer.timestamp;
    this.initialized = true;
    this.isFirstLoad = false;
  }
  evaluateQueue(dataLayer) {
    const {
      queue
    } = this;
    this.queue = [];
    queue.forEach(dataFn => {
      try {
        this.submitData(dataFn, dataLayer);
      } catch (err) {
        console.error(err);
      }
    });
  }
  submitData(dataFn, dataLayer) {
    const {
      currentWindow,
      targets
    } = this;
    const config = dataFn(dataLayer);
    const {
      flippers
    } = dataLayer || {};
    const isUseStartTransition = isFlipperOn(flippers, 'global-analytics-start-transition') || config?.useStartTransition;
    const pushData = () => {
      Object.keys(config).forEach(targetName => {
        const target = targets[targetName];
        if (target && target[config.type]) {
          target[config.type](currentWindow, dataLayer, config[targetName], config);
        }
      });
    };
    if (isUseStartTransition) {
      startTransition(() => {
        pushData();
      });
    } else {
      pushData();
    }
  }
  push(dataFn) {
    if (!this.initialized) {
      this.queue.push(dataFn);
    } else {
      this.submitData(dataFn, getDataLayer());
    }
  }
}
_AnalyticsProxy = AnalyticsProxy;
_defineProperty(AnalyticsProxy, "InstanceId", 'AnalyticsProxy');
_defineProperty(AnalyticsProxy, "getInstance", context => StateManager.getInstance(_AnalyticsProxy, context));